import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import SettingsWrapper from '~/apps/rulemanager/components/settings-wrapper/SettingsWrapper';
import { RuleInserPosition } from '~/apps/rulemanager/rulemanager-core';
import { P2GImageMessage } from '~/components/imagemessage';
import { RuleApiModel } from '~/lib/api/rulemanager/types';
import { RuleNames } from '../../../../smartsend/rules/RuleGenerator';
import {
  generateDefaultDomesticServiceRule,
  generateDefaultExtrasRule
} from '~/apps/rulemanager/lib/helpers/RuleGenerator';
import DefaultServiceFromQuote from '~/apps/smartsend/components/onboarding/setup/screens/rules/service';
import { Customer, Dimensions, QuoteApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ConfigSetting, useTaxName, useTenant, useTranslation } from '@parcel2go/npm-p2g-app';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { SmartSendProvider } from '~/lib/consts/providers';
import {
  P2GBox,
  P2GButton,
  P2GCheckBoxLegacy,
  P2GChip,
  P2GContainer,
  P2GDivider,
  P2GGrid,
  P2GIcon,
  P2GLink,
  P2GText,
  SvgChevronRight,
  SvgToggleOff,
  SvgToggleOn
} from '@parcel2go/core-ui';
import { SidePanelContent } from '~/components/sidepanel-section';
import { AppSurface } from '~/components/surface/app-surface';
import { Actions } from '~/apps/rulemanager/lib/helpers/Actions';
import ServiceCard from './service-card';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { OnboardingTitle } from './onboarding-title';
import Loading from '~/components/loading/Loading';
import OnboardingScreenSpacing from '../spacing-consts';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import { TranslationNamespaces } from '~/lib/constants';
const LazyCmsContent = lazy(() => import('~/components/content/cms-content'));

type props = {
  isXs: boolean;
  onDone: (added: string[]) => void;
  children?: React.ReactNode;
  customer?: Customer;
  initialService?: string;
  initialDimensions?: Dimensions & { Name?: string };
  initialProtection?: boolean;
  addRules: (newRules: RuleApiModel[], position: RuleInserPosition) => Promise<boolean>;
};

const allowFilters = false;

const serviceDescriptions = {
  'PARCEL2GO.UK': {
    'myhermes-parcelshop_906': 'New Lower Prices',
    'myhermes-parcelshop_907': 'New Lower Prices',
    'myhermes-parcelshop-postable_717': 'New Lower Prices',
    inpost: 'Best value for money',
    inpost_2682_2683: 'Best value for money',
    inpost_2682: 'Best value for money',
    'fedex-uk-express-dropoff_895': 'Parcel2Go Recommended'
  }
};

const SmartSendOnboardingServiceRules = ({
  isXs,
  initialDimensions,
  initialService,
  onDone,
  initialProtection,
  customer,
  addRules
}: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();
  const [quotes, setQuotes] = useState<QuoteApiModel[]>([]);
  const { settings, tenant } = useTenant();
  const [quoting, setQuoting] = useState<boolean>(false);
  const { getQuoteOptions } = useSmartSendApi();
  const [edit, setEdit] = useState(false);
  const [isPristine, setIsPristine] = useState(true);
  const [service, setService] = useState<string | null>(initialService);
  const [selectedCourierId, setSelectedCourierId] = useState<string | null>(null);
  const [collectionOnly, setCollectionOnly] = useState<boolean>(false);
  const [hasChangedService, setHasChangedService] = useState<boolean>(false);
  const [includeProtection, setIncludeProtection] = useState<boolean>(!!initialProtection);

  const serviceIdsToOffer = useMemo(() => {
    const key = `${tenant.split('.')[0]}.${tenant.split('.')[1]}`.toUpperCase();
    return Object.keys(serviceDescriptions[key] || []) || [];
  }, [tenant]);

  // Load quotes based on dimensions
  useEffect(() => {
    const getQuotes = async () => {
      setQuoting(true);
      const response = await getQuoteOptions(
        SmartSendProvider,
        SmartSendProvider,
        {
          Recipient: customer ?? { CountryIsoCode: settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY] },
          Sender: customer ?? { CountryIsoCode: settings[ConfigSetting.DEFAULT_DESTINATION_COUNTRY] },
          PackageDimensions: initialDimensions,
          MarketplaceOrderLines: [{ Value: 1, Quantity: 1 }]
        } as SmartSendItemApiModel,
        null,
        [],
        []
      );
      if (response.success) {
        setQuotes(response.result.Quotes);

        // No Service Selected, or Service from rules is not available in quotes
        if (
          response.result.Quotes.length > 0 &&
          (!service || !response.result.Quotes.some((q) => q.Service.Id === service))
        ) {
          // If there aren't any of the recommended services, pick the first available
          if (!response.result.Quotes.some((q) => serviceIdsToOffer.includes(q.Service.Id))) {
            setService(response.result.Quotes[0].Service.Id);
          }

          // The Keys are in order of preference
          let preferred: string | null = null;
          serviceIdsToOffer.forEach((sId) => {
            if (!preferred && response.result.Quotes.some((q) => q.Service.Id === sId)) {
              preferred = sId;
            }
          });

          // Use preference or first available
          setService(
            preferred || response.result.Quotes.find((q) => serviceIdsToOffer.includes(q.Service.Id))?.Service.Id
          );
        }
      }
      setQuoting(false);
    };

    const load = async () => {
      try {
        await getQuotes();
      } catch (error) {
        if (['TimeoutError', 'AbortError'].includes(error?.name)) {
          await getQuotes();
        }
      }
    };

    if (initialDimensions) {
      load();
    }
  }, [initialDimensions, customer]);

  // Filtered Quotes - Based on filter selection (currently only collection)
  const filteredQuotes = useMemo(
    () => quotes?.filter((q) => (collectionOnly ? q.Service.CollectionType === 'Collection' : true)),
    [quotes, collectionOnly]
  );

  const onAcceptDefaults = () => {
    const rules = [];

    if (service) {
      if (!rules.some((x) => x.name === RuleNames.DomesticService)) {
        rules.push(generateDefaultDomesticServiceRule(service, settings[ConfigSetting.DEFAULT_ORIGIN_COUNTRY]));
      }
    }

    if (includeProtection) {
      if (!rules.some((x) => x.name === RuleNames.Extras)) {
        rules.push(generateDefaultExtrasRule([Actions.Extra_ExtendedBaseCover, Actions.Extra_Cover]));
      }
    }

    if (rules.length) {
      addRules(rules, 'first');
    }

    onDone(rules.map((r) => r.name));
  };

  // Used when reverting to recommended services
  const serviceIdsToOfferThatHaveQuotes = useMemo(
    () => serviceIdsToOffer.filter((sId) => quotes.some((q) => q.Service.Id === sId)),
    [quotes, serviceIdsToOffer]
  );

  // Limit to Suggestions when not editing
  const serviceSuggestions = useMemo(() => {
    const output = quotes.filter((q) =>
      hasChangedService
        ? q.Service.Id === service
        : !serviceIdsToOfferThatHaveQuotes.length || serviceIdsToOfferThatHaveQuotes.includes(q.Service.Id)
    );

    // Order by serviceIdsToOffer
    if (!hasChangedService) {
      output.sort((a, b) => {
        const aIndex = serviceIdsToOfferThatHaveQuotes.indexOf(a.Service.Id);
        const bIndex = serviceIdsToOfferThatHaveQuotes.indexOf(b.Service.Id);
        return aIndex - bIndex;
      });
    } else {
      // Order by Service.Name alphabetically
      output.sort((a, b) => a.Service.Name.localeCompare(b.Service.Name));
    }

    return output;
  }, [quotes, hasChangedService, service, serviceIdsToOfferThatHaveQuotes]);

  const selectedService = useMemo(() => quotes.find((s) => s.Service.Id === service), [quotes, service]);

  const reset = () => {
    setEdit(false);
    setCollectionOnly(false);
    setService(initialService || serviceIdsToOfferThatHaveQuotes[0]);
    setSelectedCourierId(null);
    setHasChangedService(false);
    setIsPristine(true);
  };

  const Message = ({ selectedService }: { selectedService: QuoteApiModel }) => (
    <>
      {selectedService ? (
        <P2GText variant="subtitle2" component="div">
          We'll use {selectedService.Service.Name} as your default courier service
        </P2GText>
      ) : (
        <P2GText variant="subtitle2" component="div">
          Select a service
        </P2GText>
      )}
      <P2GText>You can change this later from your dashboard.</P2GText>
    </>
  );

  if (!quoting && quotes?.length === 0) {
    return (
      <>
        <OnboardingScreenContentWrapper>
          <P2GContainer maxWidth="md">
            <SidePanelContent>
              <OnboardingTitle
                text="Set your default <span>service</span>"
                translationKey="SETUP_SERVICE_RULES_TITLE"
              />
              <P2GText variant="subtitle1">
                Unfortunately, we couldn't find any suitable services for your shipment.
                <br />
                When your items are imported we'll check again for suitable services.
              </P2GText>
            </SidePanelContent>
          </P2GContainer>
        </OnboardingScreenContentWrapper>
        <OnboardingActionBar
          skip={
            <P2GButton variant="text" onClick={() => onDone([])}>
              {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
            </P2GButton>
          }
        >
          {!isXs && (
            <P2GText variant="subtitle2">
              Unfortunately, we couldn't find any suitable services for your shipment.
            </P2GText>
          )}
        </OnboardingActionBar>
      </>
    );
  }

  return (
    <SettingsWrapper
      overrideLoading={
        <>
          <OnboardingScreenContentWrapper>
            <P2GContainer maxWidth="md">
              <SidePanelContent>
                <OnboardingTitle
                  text="Set your default <span>service</span>"
                  translationKey="SETUP_SERVICE_RULES_TITLE"
                />
                <P2GText variant="subtitle1">Just getting things ready for you...</P2GText>
                <Loading />
              </SidePanelContent>
            </P2GContainer>
          </OnboardingScreenContentWrapper>
          <OnboardingActionBar>
            <P2GText variant="subtitle2" component="div">
              Just getting things ready for you...
            </P2GText>
          </OnboardingActionBar>
        </>
      }
    >
      <OnboardingScreenContentWrapper>
        <P2GContainer maxWidth="md">
          <SidePanelContent>
            <OnboardingTitle text="Set your default <span>service</span>" translationKey="SETUP_SERVICE_RULES_TITLE" />
            <P2GText variant="subtitle1">
              We’ll set up a rule so your account automatically uses your preferred courier and service.
              <br />
              And don’t worry, you can change this anytime.
            </P2GText>
          </SidePanelContent>
        </P2GContainer>
        <P2GBox sx={OnboardingScreenSpacing} textAlign="left">
          <P2GContainer maxWidth="md">
            <AppSurface>
              <P2GGrid container direction="row" wrap="nowrap" spacing={4}>
                {!isXs && (
                  <Suspense>
                    <LazyCmsContent
                      contentKey="smartsend-setup-rules-service-image"
                      wrapper={(x) => <P2GGrid item>{x}</P2GGrid>}
                    />
                  </Suspense>
                )}
                <P2GGrid item flexGrow={1}>
                  {quoting ? (
                    <P2GImageMessage message="Grabbing some prices for your default box size..." showLoader />
                  ) : (
                    <P2GBox display="flex" flexDirection="column" gap={2}>
                      <P2GText variant="subtitle1" fontWeight="bold">
                        Courier Selection
                      </P2GText>
                      <P2GBox textAlign="left">
                        <P2GText variant="subtitle2">{selectedService?.Service?.Name ?? 'No Service Selected'}</P2GText>
                      </P2GBox>
                      {((isPristine && serviceIdsToOfferThatHaveQuotes.length > 0) ||
                        serviceIdsToOfferThatHaveQuotes.includes(service)) &&
                        !edit && (
                          <P2GBox>
                            <P2GChip color="success" label="Default service selected" />
                          </P2GBox>
                        )}
                      <P2GDivider />
                      <P2GBox mt={2} mb={2} display="flex" alignItems="center" gap={1} justifyContent="space-between">
                        <P2GText display="flex" alignItems="center" justifyContent="flex-start">
                          <P2GIcon size="sm">
                            <SvgChevronRight />
                          </P2GIcon>
                          <P2GLink onClick={reset}>
                            {serviceIdsToOfferThatHaveQuotes.length === 0 ? 'View Services' : 'Recommended Services'}
                          </P2GLink>
                          {edit && (
                            <>
                              <P2GIcon size="sm">
                                <SvgChevronRight />
                              </P2GIcon>
                              <P2GLink
                                onClick={() => {
                                  setEdit(true);
                                  setService(null);
                                  setSelectedCourierId(null);
                                }}
                              >
                                Other Services
                              </P2GLink>
                            </>
                          )}
                        </P2GText>
                        <P2GText display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                          <P2GIcon onClick={() => setIncludeProtection(!includeProtection)} size="sm">
                            {includeProtection ? <SvgToggleOn /> : <SvgToggleOff />}
                          </P2GIcon>
                          Fully protect my items
                        </P2GText>
                      </P2GBox>
                      {edit ? (
                        <>
                          {allowFilters && quotes?.some((x) => x.Service.CollectionType === 'Collection') && (
                            <P2GCheckBoxLegacy
                              value={collectionOnly}
                              onChange={() => {
                                setCollectionOnly(!collectionOnly);
                                setService(initialService || serviceIdsToOfferThatHaveQuotes[0]);
                                setSelectedCourierId(null);
                                setHasChangedService(false);
                                setIsPristine(false);
                              }}
                              id="Collection"
                              checked={collectionOnly}
                              label="Only show couriers that collect from my address"
                            />
                          )}
                          <DefaultServiceFromQuote
                            isXs={isXs}
                            quotes={filteredQuotes}
                            showSelectedCourier={false}
                            selectedServiceId={service}
                            selectedCourierId={selectedCourierId}
                            includeProtection={includeProtection}
                            onSave={(s, c) => {
                              setService(s);
                              setSelectedCourierId(c);
                              setHasChangedService(!!s && !!c);
                              setIsPristine(false);
                            }}
                          />
                          <P2GBox mt={1} display="flex" justifyContent="flex-end">
                            <P2GText variant="caption">* Prices exc {taxName}</P2GText>
                          </P2GBox>
                        </>
                      ) : (
                        <>
                          <P2GBox display="flex" flexDirection="column" gap={1}>
                            {serviceSuggestions.map((q) => (
                              <ServiceCard
                                isXs={isXs}
                                key={q.Service.Id}
                                quote={q}
                                includeProtection={includeProtection}
                                tagline={serviceDescriptions[q.Service.Id] || ''}
                                onClick={() => {
                                  setService(q.Service.Id);
                                  setSelectedCourierId(q.Service.CourierId);
                                  setIsPristine(false);
                                }}
                                selected={service === q.Service.Id}
                              />
                            ))}
                          </P2GBox>
                          <P2GBox mt={1} display="flex" alignItems="center" gap={1} justifyContent="space-between">
                            <P2GLink onClick={() => setEdit(true)}>View other couriers</P2GLink>
                            <P2GText variant="caption">* Prices exc {taxName}</P2GText>
                          </P2GBox>
                        </>
                      )}
                    </P2GBox>
                  )}
                </P2GGrid>
              </P2GGrid>
            </AppSurface>
            {isXs && (
              <P2GBox mt={2}>
                <AppSurface>
                  <P2GBox textAlign="center">
                    <Message selectedService={selectedService} />
                  </P2GBox>
                </AppSurface>
              </P2GBox>
            )}
          </P2GContainer>
        </P2GBox>
      </OnboardingScreenContentWrapper>
      <OnboardingActionBar
        next={
          <P2GButton
            size="large"
            variant="primary"
            fullWidth={isXs}
            disabled={!selectedService}
            onClick={onAcceptDefaults}
            style={{ minWidth: 'unset' }}
          >
            Continue
          </P2GButton>
        }
        skip={
          <P2GButton variant="text" onClick={() => onDone([])}>
            {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
          </P2GButton>
        }
      >
        {!isXs && <Message selectedService={selectedService} />}
      </OnboardingActionBar>
    </SettingsWrapper>
  );
};

export default SmartSendOnboardingServiceRules;
